<template>
  <div class="row mt-3">
    <MaterialUsageModal :modalId="'jobDetailMaterialUsage'" />

    <div class="col">
      <b-button v-if="products.length" variant="outline-secondary" @click="show = !show" class="col-12 mt-5">
        {{ show ? $t('jobs.detail.productListHide') : $t('jobs.detail.productListShow') }}
      </b-button>
      <b-collapse v-model="show" class="mt-2">
        <div v-if="loading" class="text-center py-3">
          <i class="fas fa-spinner fa-spin loading-spinner"></i>
        </div>
        <table v-else class="table">
          <thead>
            <tr>
              <th>{{ $t('jobs.detail.productTable.productNumber') }}</th>
              <th>{{ $t('jobs.detail.productTable.amountProduced') }}</th>
              <th v-if="hasMaterialUsage"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in computedProducts" :key="product.id || index">
              <td>{{ product.productNumber }}</td>
              <td>
                <span v-if="loadingProducts[product.id]">
                  <i class="fas fa-spinner fa-spin"></i>
                </span>
                <span v-else>
                  <i :class="['fas fa-check-square', getStatusClass(product)]"></i>
                  <span class="ml-2">{{ product.amountProduced }} / {{ product.amount }}</span>
                </span>
              </td>
              <td v-if="hasMaterialUsage">
                <i v-if="!loadingProducts[product.id] && (getClaims.admin || getClaims.teamlead || getClaims.planner || getClaims.manager)"
                   class="far fa-edit edit-icon"
                   @click="openMaterialUsage(product)"></i>
              </td>
              <td v-else></td>
            </tr>
          </tbody>
        </table>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import MaterialUsageModal from '@/components/workflowInterface/subViews/ActiveOrder/MaterialUsage.vue';

export default {
  name: 'JobProductAmounts',
  components: { MaterialUsageModal },
  props: {
    job: {
      required: true,
      type: Object
    }
  },
  data() {
    return {
      show: false,
      loading: false,
      hasMaterialUsage: false,
      loadingProducts: {},
    };
  },
  computed: {
    ...mapGetters('users', ['getClaims']),
    ...mapGetters('materialUsage', ['getMaterialUsageOfJob', 'getMaterialUsageOfJobProduct']),
    ...mapGetters('productionRuns', ['getProductionRunsOfJob']),
    products() {
      return this.job.products || [];
    },
    getActiveRun() {
      const runs = this.getProductionRunsOfJob(this.job.id);
      return _.find(runs, r => !r.productionEnd) || _.last(runs) || null;
    },
    computedProducts() {
      return _.orderBy(this.products.map(product => {
        let amountProduced;
        if (this.hasMaterialUsage) {
          const materialUsage = this.getMaterialUsageOfJobProduct(product.id);
          amountProduced = _.sumBy(materialUsage, 'amountProduced');
        } else {
          amountProduced = product.amountProduced || 0;
        }
        return {
          ...product,
          amountProduced,
        };
      }), ['sequence'], ['asc']);
    }
  },
  methods: {
    getStatusClass(product) {
      if (product.amountProduced >= product.amount) return 'done';
      if (product.amountProduced > 0) return 'incomplete';
      return '';
    },
    openMaterialUsage(product) {
      window.$('#jobDetailMaterialUsage').data('parameters', {
        productRow: product,
        activeRun: this.getActiveRun,
      });
      window.$('#jobDetailMaterialUsage').modal('show');
    }
  },
  async created() {
    if (!this.products.length) return;
    this.loading = true;
    try {
      const where = [
        ['companyId', '==', this.getClaims.companyId],
        ['jobId', '==', this.job.id]
      ];
      await Promise.all([
        this.$store.dispatch('timeTracking/fetchAndAdd', { where }),
        this.$store.dispatch('productionRuns/fetchAndAdd', { where }),
      ]);
    } catch (e) {
      console.error('JobProductAmounts: failed to fetch initial data', e);
    } finally {
      this.loading = false;
    }

    // Mark all products as loading before the loop so spinners appear immediately
    this.products.forEach(product => {
      this.$set(this.loadingProducts, product.id, true);
    });

    // Fetch material usage per product one by one
    for (const product of this.products) {
      try {
        const where = [
          ['companyId', '==', this.getClaims.companyId],
          ['productJobId', '==', product.id]
        ];
        await this.$store.dispatch('materialUsage/fetchAndAdd', { where });
        if (!this.hasMaterialUsage && this.getMaterialUsageOfJobProduct(product.id).length > 0) {
          this.hasMaterialUsage = true;
        }
      } catch (e) {
        console.error(`JobProductAmounts: failed to fetch material usage for product ${product.id}`, e);
      } finally {
        this.$set(this.loadingProducts, product.id, false);
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../assets/scss/MotivateColors";

.edit-icon {
  cursor: pointer;
  color: $besWelBlack;
}

.loading-spinner {
  font-size: 28px;
  color: $besWelBlack;
}

.fa-check-square {
  font-size: 18px;
  color: $grey;

  &.done {
    color: $superSuccess;
  }

  &.incomplete {
    color: $brightYellow;
  }
}
</style>
